import api from "@/api";
import { defineStore } from "pinia";
import { auth } from "@/utils/firebase";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useDataStore } from "./dataStore";
import { Admin } from "@/interfaces/admin.interface";
import { useFilterStore } from "./filterStore";

export const useUserStore = defineStore({
  id: "userStore",
  state: () => ({
    loader: {
      login: false,
      logout: false,
      registration: false,
      updateProfile: false,
      resetPassword: false,
      refreshSignupLink: false
    },
    user: null as Admin | null,
  }),
  getters: {
    getUser(): Admin | null {
      return this.user;
    },
    /**
     * Returns an array of role names the user has.
     * Example: ['Class Group Admin', 'School Group Admin']
     */
    getAdminTypes(): string[] {
      return this.user?.roles.map((role) => role.name) || [];
    },
    /**
     * Returns an array of ClassGroup IDs the admin has access to.
     * Relevant for roles like 'Class Group Admin'.
     */
    getAdminClassGroupIds(): string[] {
      const classGroupIds: string[] = [];
      if (this.isClassGroupAdmin) {
        this.user?.roles.forEach((role) => {
          if (role.permissions) {
            role.permissions.forEach((permission) => {
              if (
                permission.resourceType === "ClassGroup" &&
                permission.resourceIds.length > 0
              ) {
                classGroupIds.push(...permission.resourceIds);
              }
            });
          }
        });
      }

      if (this.isSchoolAdmin) {
        // Return all class groups in the selected school and grade
        const filterStore = useFilterStore();
        const school = filterStore.selectedSchool;
        const grade = filterStore.selectedGrade;

        if (school && grade) {
          const classGroups = school.classes.filter(
            (cg) => cg.gradeLevel === grade
          );
          classGroupIds.push(...classGroups.map((cg) => cg._id));
        }
      }

      if (this.isInternalAdmin) {
        // Return all class groups in the selected school and grade
        const filterStore = useFilterStore();
        const school = filterStore.selectedSchool;
        const grade = filterStore.selectedGrade;

        if (school && grade) {
          const classGroups = school.classes.filter(
            (cg) => cg.gradeLevel === grade
          );
          classGroupIds.push(...classGroups.map((cg) => cg._id));
        }
      }

      return classGroupIds;
    },
    /**
     * Returns an array of SchoolGroup IDs the admin has access to.
     * Relevant for roles like 'School Group Admin'.
     */
    getAdminSchoolGroupIds(): string[] {
      const schoolGroupIds: string[] = [];
      this.user?.roles.forEach((role) => {
        if (role.permissions) {
          role.permissions.forEach((permission) => {
            if (
              permission.resourceType === "SchoolGroup" &&
              permission.resourceIds.length > 0
            ) {
              schoolGroupIds.push(...permission.resourceIds);
            }
          });
        }
      });
      return schoolGroupIds;
    },
    /**
     * Returns an array of School IDs the admin has access to.
     * Relevant for roles like 'School Admin'.
     */
    getAdminSchoolIds(): string[] {
      const schoolIds: string[] = [];
      this.user?.roles.forEach((role) => {
        if (role.permissions) {
          role.permissions.forEach((permission) => {
            if (
              permission.resourceType === "School" &&
              permission.resourceIds.length > 0
            ) {
              schoolIds.push(...permission.resourceIds);
            }
          });
        }
      });
      return schoolIds;
    },
    isInternalAdmin(): boolean {
      return this.getAdminTypes.includes("Internal Admin");
    },
    isSchoolGroupAdmin(): boolean {
      return this.getAdminTypes.includes("School Group Champion") || this.getAdminTypes.includes("School Group Admin");
    },
    isSchoolAdmin(): boolean {
      return this.getAdminTypes.includes("School Champion") || this.getAdminTypes.includes("School Admin");
    },
    isMultipleSchoolAdmin(): boolean {
      if (this.getAdminTypes.includes("School Champion")) {
        const schoolIds = this.user?.roles.flatMap((role) => 
          role.permissions
            .filter((permission) => permission.resourceType === "School")
            .flatMap((permission) => permission.resourceIds)
        ) || [];
        return schoolIds.length > 1;
      }
      return false;
    },
    isClassGroupAdmin(): boolean {
      return this.getAdminTypes.includes("Class Group Admin");
    },
  },
  actions: {
    /**
     * Login user
     * @param loginDetails email and password
     */
    async login(loginDetails: { email: string; password: string }) {
      this.loader.login = true;

      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          loginDetails.email,
          loginDetails.password
        );

        if (!userCredential) {
          throw new Error("Firebase user not found");
        }

        const userResponse = await api.get(
          `v2/access/admin/single/${userCredential.user?.uid}`
        );

        if (userResponse.status !== 200) {
          throw new Error("Failed to fetch user data from API.");
        }

        const userData = userResponse.data.data;
        this.user = userData;

        // Fetch initial data after login
        const dataStore = useDataStore();
        await dataStore.fetchInitialData(true);
      } catch (error: any) {
        if (error.code) {
          switch (error.code) {
            case "auth/wrong-password":
              alert(
                "The password is invalid or the user does not have a password."
              );
              break;

            case "auth/user-not-found":
              alert(
                "There is no user record corresponding to this identifier. The user may have been deleted."
              );
              break;

            case "auth/too-many-requests":
              alert(
                "Too many unsuccessful login attempts. Please try again later."
              );
              break;

            // API Errors
            case "ERR_BAD_REQUEST":
              if (error.response.status === 404) {
                alert("User not found. Please try again.");
              } else {
                alert("An unexpected error occurred. Please try again.");
              }
              break;

            default:
              alert("An unexpected error occurred. Please try again.");
              break;
          }
        }

        this.user = null;
        return false;
      } finally {
        this.loader.login = false;
      }
      return true;
    },

    /**
     *Get user id
     *@param userId
     */
    async googleLogin(userId: string) {
      try {
        const userResponse = await api.get(`v2/access/admin/single/${userId}`);
        console.log("userResponse: ", userResponse);

        if (userResponse.status !== 200) {
          throw new Error("Failed to fetch user data from API.");
        }

        const userData = userResponse.data.data;
        this.user = userData;

        // Fetch initial data after login
        const dataStore = useDataStore();
        await dataStore.fetchInitialData(true);
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          switch (error.code) {
            case "auth/wrong-password":
              alert(
                "The password is invalid or the user does not have a password."
              );
              break;

            case "auth/user-not-found":
              alert(
                "There is no user record corresponding to this identifier. The user may have been deleted."
              );
              break;

            case "auth/too-many-requests":
              alert(
                "Too many unsuccessful login attempts. Please try again later."
              );
              break;

            // API Errors
            case "ERR_BAD_REQUEST":
              if (error.response.status === 404) {
                alert("User not found. Please try again.");
              } else {
                alert("An unexpected error occurred. Please try again.");
              }
              break;

            default:
              alert("An unexpected error occurred. Please try again.");
              break;
          }
        }

        this.user = null;
        return false;
      }

      return true;
    },

    /**
     * Reset password
     * @param email user's email address
     */
    async resetPassword(email: string) {
      this.loader.resetPassword = true;
      try {
        await sendPasswordResetEmail(auth, email);
        alert("Password reset email sent successfully.");
      } catch (error: any) {
        console.error("Error sending password reset email: ", error);
        alert("Failed to send password reset email. Please try again.");
      } finally {
        this.loader.resetPassword = false;
      }
    },

    /**
     * Get admin account
     * @param userToken
     */
    async getAdminAccount(userToken: string) {
      let adminEmail = "";

      try {
        const userResponse = await api.get(
          `v2/access/admin/get-initialised-admin?registrationToken=${userToken}`
        );

        if (userResponse.status !== 200) {
          throw new Error("Failed to fetch user data from API.");
        }

        adminEmail = userResponse.data.data.email;
      } catch (error: any) {
        console.log("Error: ", error);
        throw error;
      }

      return adminEmail;
    },

    async completeRegistration(userDetails: {
      token: string;
      userPassword: string;
      courtesyTitle: string;
    }) {
      this.loader.registration = true;

      try {
        const userResponse = await api.post(
          `v2/access/admin/complete-registration?registrationToken=${userDetails.token}`,
          {
            password: userDetails.userPassword,
            courtesyTitle: userDetails.courtesyTitle,
          }
        );

        if (userResponse.status !== 200) {
          throw new Error("Failed to fetch user data from API.");
        }

        const userData = userResponse.data.data;
        this.user = userData;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          switch (error.code) {
            case "ERR_BAD_REQUEST":
              if (error.response.status === 404) {
                alert("User not found. Please try again.");
              } else {
                alert("An unexpected error occurred. Please try again.");
              }
              break;

            default:
              alert("An unexpected error occurred. Please try again.");
              break;
          }
        }

        return false;
      } finally {
        this.loader.registration = false;
      }

      return true;
    },

    async selfRefreshRegistrationToken({ oldToken, email }: { oldToken: string, email: string }) {
      try {
        this.loader.refreshSignupLink = true;
        const { data } = await api.post(
          "/v2/access/admin/self-refresh-registration-link",
          {
            oldRegistrationToken: oldToken,
            email
          }
        );
        this.loader.refreshSignupLink = false;
        return data;
      } catch (err) {
        console.log(err)
        throw err;
      } finally {
        this.loader.refreshSignupLink = false;
      }
    },

    /**
     * Logout user
     */
    async logout() {
      const filterStore = useFilterStore();

      try {
        await auth.signOut();
        filterStore.resetFilters();
        this.user = null;
        return true;
      } catch (error) {
        console.log("Error: ", error);
        return false;
      }
    },

    /**
     * Update user profile
     * @param updatedData Object containing updated user data
     */
    async updateProfile(updatedData: Partial<Admin>) {
      this.loader.updateProfile = true;

      try {
        const currentUser = this.user;

        if (!currentUser) {
          throw new Error("No user is logged in.");
        }

        // Send updated data to your backend API
        const response = await api.put(
          `v2/access/admin/update/${currentUser._id}`,
          updatedData
        );

        if (response.status !== 200) {
          throw new Error("Failed to update user data on API.");
        }

        // Update the user in the store
        this.user = response.data.data;

        return true;
      } catch (error: any) {
        // Handle specific Firebase Auth errors
        if (error.code === "auth/wrong-password") {
          alert("Incorrect password. Please try again.");
        } else if (error.code === "auth/email-already-in-use") {
          alert("The email address is already in use by another account.");
        } else {
          alert("Failed to update profile.");
        }

        return false;
      } finally {
        this.loader.updateProfile = false;
      }
    },
  },
  persist: true,
});
